import React, { useEffect, useMemo, useState } from 'react';
import { Card, Form, Input, DatePicker, Select, Button, message, Table, Tag, Row, Col, Divider, Popconfirm, Space, Modal } from 'antd';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { createMatch, fetchMatchesByAgeGroup, deleteMatch, updateMatch } from '../../store/slices/matchesSlice';
import { useAgeGroups } from '../../hooks/useAgeGroups';

const { Option } = Select;

const Matches = () => {
    const [form] = Form.useForm();
    const dispatch = useDispatch();
    const selectedBranch = useSelector((state) => state.branches.selectedBranch);
    const { ageGroups } = useAgeGroups(selectedBranch?.id);
    const { loading } = useSelector((state) => state.matches);
    const [matches, setMatches] = useState([]);
    const [listAgeGroupIds, setListAgeGroupIds] = useState([]);
    const [editingMatch, setEditingMatch] = useState(null);
    const [isEditModalVisible, setIsEditModalVisible] = useState(false);
    const summary = useMemo(() => {
        const initial = { G: 0, B: 0, M: 0 };
        (matches || []).forEach((m) => {
            if (m?.result && initial.hasOwnProperty(m.result)) {
                initial[m.result] += 1;
            }
        });
        return initial;
    }, [matches]);

    const resultOptions = useMemo(() => ([
        { value: 'G', label: 'Galibiyet' },
        { value: 'B', label: 'Beraberlik' },
        { value: 'M', label: 'Mağlubiyet' },
    ]), []);

    const matchTypeOptions = useMemo(() => ([
        { value: 0, label: 'Hazırlık' },
        { value: 1, label: 'Lig Maçı' },
        { value: 2, label: 'Turnuva' },
        { value: 3, label: 'Diğer' },
    ]), []);

    const onFinish = async (values) => {
        try {
            const payload = {
                ageGroupId: values.ageGroupId,
                location: values.location,
                result: values.result,
                score: values.score,
                date: values.date ? values.date.toISOString() : dayjs().toISOString(),
                rival: values.rival,
                note: values.note,
                matchType: values.matchType,
            };

            await dispatch(createMatch(payload)).unwrap();
            message.success('Maç başarıyla eklendi');
            form.resetFields();
            if ((listAgeGroupIds || []).length > 0) {
                dispatch(fetchMatchesByAgeGroup({ ageGroupIds: listAgeGroupIds })).unwrap().then((data) => {
                    setMatches(data?.items || []);
                }).catch((err) => {
                    setMatches([]);
                    message.error(`Hata: ${err}`);
                });
            }
        } catch (err) {
            message.error(`Hata: ${err}`);
        }
    };

    const fetchMatches = (ageGroupIds) => {
        dispatch(fetchMatchesByAgeGroup({ ageGroupIds })).unwrap().then((data) => {
            setMatches(data?.items || []);
        }).catch((err) => {
            setMatches([]);
            message.error(`Hata: ${err}`);
        });
    }

    // Formdaki yaş grubu listeyi tetiklemesin; aşağıdaki selector kullanılacak
    const handleListAgeGroupChange = (vals) => {
        setListAgeGroupIds(vals || []);
        if ((vals || []).length > 0) fetchMatches(vals); else setMatches([]);
    };

    const handleSelectAllAgeGroups = () => {
        const allIds = (ageGroups || []).map((ag) => ag.id);
        setListAgeGroupIds(allIds);
        if (allIds.length > 0) fetchMatches(allIds); else setMatches([]);
    };

    const handleDelete = (id) => {
        dispatch(deleteMatch(id)).unwrap().then((data) => {
            message.success('Maç başarıyla silindi');
            fetchMatches(listAgeGroupIds);
        }).catch((err) => {
            message.error(`Hata: ${err}`);
        });
    };

    const handleEdit = (record) => {
        setEditingMatch(record);
        setIsEditModalVisible(true);
        
        // Form'u mevcut değerlerle güncelle
        form.setFieldsValue({
            ageGroupId: record.ageGroupId,
            location: record.location,
            result: record.result,
            score: record.score,
            date: record.date ? dayjs(record.date) : null,
            rival: record.rival,
            note: record.note,
            matchType: record.matchType,
        });
    };

    const handleEditCancel = () => {
        setEditingMatch(null);
        setIsEditModalVisible(false);
        form.resetFields();
    };

    const handleEditSubmit = async (values) => {
        try {
            const payload = {
                id: editingMatch.id,
                ageGroupId: values.ageGroupId,
                location: values.location,
                result: values.result,
                score: values.score,
                date: values.date ? values.date.toISOString() : dayjs().toISOString(),
                rival: values.rival,
                note: values.note,
                matchType: values.matchType,
            };

            await dispatch(updateMatch(payload)).unwrap();
            message.success('Maç başarıyla güncellendi');
            handleEditCancel();
            // Refresh the matches list with current selected age groups
            if ((listAgeGroupIds || []).length > 0) {
                dispatch(fetchMatchesByAgeGroup({ ageGroupIds: listAgeGroupIds })).unwrap().then((data) => {
                    setMatches(data?.items || []);
                }).catch((err) => {
                    setMatches([]);
                    message.error(`Hata: ${err}`);
                });
            }
        } catch (err) {
            message.error(`Hata: ${err}`);
        }
    };

    const columns = [
        {
            title: 'Tarih',
            dataIndex: 'date',
            key: 'date',
            render: (value) => value ? dayjs(value).format('DD.MM.YYYY HH:mm') : '-'
        },
        {
            title: 'Rakip',
            dataIndex: 'rival',
            key: 'rival',
        },
        {
            title: 'Konum',
            dataIndex: 'location',
            key: 'location',
        },
        {
            title: 'Skor',
            dataIndex: 'score',
            key: 'score',
            render: (val) => val || '-',
        },
        {
            title: 'Yaş Grubu',
            dataIndex: 'ageGroup',
            key: 'ageGroup',
        },
        {
            title: 'Maç Tipi',
            dataIndex: 'matchType',
            key: 'matchType',
            render: (value) => {
                const matchType = matchTypeOptions.find(opt => opt.value === value);
                return matchType ? matchType.label : '-';
            },
            filters: matchTypeOptions.map(opt => ({ text: opt.label, value: opt.value })),
            onFilter: (value, record) => record.matchType === value,
        },
        {
            title: 'Not',
            dataIndex: 'note',
            key: 'note',
            render: (val) => val || '-',
        },
        {
            title: 'Sonuç',
            dataIndex: 'result',
            key: 'result',
            render: (val) => {
                const map = { G: { color: 'green', text: 'Galibiyet' }, B: { color: 'gold', text: 'Beraberlik' }, M: { color: 'red', text: 'Mağlubiyet' } };
                const meta = map[val] || { color: 'default', text: val };
                return <Tag color={meta.color}>{meta.text}{meta.text !== val ? ` (${val})` : ''}</Tag>;
            }
        },
        {
            title: 'İşlemler',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button type="primary" onClick={() => handleEdit(record)}>Düzenle</Button>
                    <Popconfirm title="Bu maçı silmek istediğinizden emin misiniz?" onConfirm={() => handleDelete(record.id)} okText="Evet" cancelText="Hayır"> <Button danger>Sil</Button> </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <>
            <Card title="Maç Ekle" bordered>
                <Form form={form} layout="vertical" onFinish={onFinish}>
                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <Form.Item name="ageGroupId" label="Yaş Grubu" rules={[{ required: true, message: 'Lütfen yaş grubu seçiniz' }]}>
                                <Select placeholder="Yaş grubu seçiniz" showSearch optionFilterProp="label">
                                    {ageGroups?.map((ag) => (
                                        <Option key={ag.id} value={ag.id} label={ag.name}>{ag.name}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item name="date" label="Tarih" rules={[{ required: true, message: 'Lütfen tarih seçiniz' }]}>
                                <DatePicker style={{ width: '100%' }} showTime format="DD.MM.YYYY HH:mm" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col xs={24} md={8}>
                            <Form.Item name="rival" label="Rakip" rules={[{ required: true, message: 'Lütfen rakip takım adını giriniz' }]}>
                                <Input placeholder="Rakip takım" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item name="location" label="Konum" rules={[{ required: true, message: 'Lütfen konum giriniz' }]}>
                                <Input placeholder="Örn: Stadyum" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item name="result" label="Sonuç" >
                                <Select placeholder="Sonuç seçiniz">
                                    {resultOptions.map(opt => (
                                        <Option key={opt.value} value={opt.value}>{`${opt.label} (${opt.value})`}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col xs={24} md={8}>
                            <Form.Item name="score" label="Skor">
                                <Input placeholder="Örn: 2-1" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item name="matchType" label="Maç Tipi" rules={[{ required: true, message: 'Lütfen maç tipi seçiniz' }]}>
                                <Select placeholder="Maç tipi seçiniz">
                                    {matchTypeOptions.map(opt => (
                                        <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item name="note" label="Not">
                                <Input placeholder="Not" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item>
                        <Button type="primary" htmlType="submit">Kaydet</Button>
                    </Form.Item>
                </Form>
            </Card>

            <Divider />

            <Card title="Seçilen Yaş Grubunun Maçları" bordered>
                <Row gutter={16} style={{ marginBottom: 12 }}>
                    <Col xs={24} md={24}>
                        <Form layout="vertical">
                            <Form.Item label="Yaş Grubu (Liste)">
                                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                    <Select
                                        placeholder="Liste için yaş grubu seçiniz"
                                        value={listAgeGroupIds}
                                        onChange={handleListAgeGroupChange}
                                        allowClear
                                        mode="multiple"
                                        showSearch
                                        optionFilterProp="label"
                                        style={{ flex: 1, minWidth: 280 }}
                                    >
                                        {ageGroups?.map((ag) => (
                                            <Option key={ag.id} value={ag.id} label={ag.name}>{ag.name}</Option>
                                        ))}
                                    </Select>
                                    <Button onClick={handleSelectAllAgeGroups} disabled={!(ageGroups || []).length}>
                                        Hepsini Seç
                                    </Button>
                                </div>
                            </Form.Item>
                        </Form>
                    </Col>
                </Row>
                <Row gutter={16} style={{ marginBottom: 12 }}>
                    <Col xs={24} md={8}>
                        <Card size="small" bordered>
                            Galibiyet: <Tag color="green">{summary.G}</Tag>
                        </Card>
                    </Col>
                    <Col xs={24} md={8}>
                        <Card size="small" bordered>
                            Beraberlik: <Tag color="gold">{summary.B}</Tag>
                        </Card>
                    </Col>
                    <Col xs={24} md={8}>
                        <Card size="small" bordered>
                            Mağlubiyet: <Tag color="red">{summary.M}</Tag>
                        </Card>
                    </Col>
                </Row>
                <Table
                    rowKey={(r) => r.id || `${r.ageGroupId}-${r.date}-${r.rival}`}
                    columns={columns}
                    dataSource={matches}
                    loading={loading}
                    pagination={{ pageSize: 10 }}
                />
            </Card>

            {/* Edit Match Modal */}
            <Modal
                title="Maç Düzenle"
                open={isEditModalVisible}
                onCancel={handleEditCancel}
                footer={null}
                width={800}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleEditSubmit}
                >
                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <Form.Item name="ageGroupId" label="Yaş Grubu" rules={[{ required: true, message: 'Lütfen yaş grubu seçiniz' }]}>
                                <Select placeholder="Yaş grubu seçiniz" showSearch optionFilterProp="label">
                                    {ageGroups?.map((ag) => (
                                        <Option key={ag.id} value={ag.id} label={ag.name}>{ag.name}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item name="date" label="Tarih" rules={[{ required: true, message: 'Lütfen tarih seçiniz' }]}>
                                <DatePicker style={{ width: '100%' }} showTime format="DD.MM.YYYY HH:mm" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col xs={24} md={8}>
                            <Form.Item name="rival" label="Rakip" rules={[{ required: true, message: 'Lütfen rakip takım adını giriniz' }]}>
                                <Input placeholder="Rakip takım" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item name="location" label="Konum" rules={[{ required: true, message: 'Lütfen konum giriniz' }]}>
                                <Input placeholder="Örn: Stadyum" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item name="result" label="Sonuç">
                                <Select placeholder="Sonuç seçiniz">
                                    {resultOptions.map(opt => (
                                        <Option key={opt.value} value={opt.value}>{`${opt.label} (${opt.value})`}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col xs={24} md={8}>
                            <Form.Item name="score" label="Skor">
                                <Input placeholder="Örn: 2-1" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item name="matchType" label="Maç Tipi" rules={[{ required: true, message: 'Lütfen maç tipi seçiniz' }]}>
                                <Select placeholder="Maç tipi seçiniz">
                                    {matchTypeOptions.map(opt => (
                                        <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item name="note" label="Not">
                                <Input placeholder="Not" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                        <Space>
                            <Button onClick={handleEditCancel}>
                                İptal
                            </Button>
                            <Button type="primary" htmlType="submit">
                                Güncelle
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};

export default Matches;


